import { useAudienceUploadLimit } from 'hooks/useAudienceUploadLimit'
import { useEnterpriseSupport } from 'hooks/useEnterpriseSupport'
import moment from 'moment/moment'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { EuiButton, EuiCallOut, EuiCheckbox, EuiFieldNumber, EuiFieldText, EuiFlexGrid, EuiFlexGroup, EuiFlexItem, EuiFormRow, EuiLink, EuiLoadingContent, EuiSpacer, EuiText } from '@elastic/eui'

import { AudienceFootprint, Campaign, CampaignActiveStatus, CampaignStatus, CampaignType, CustomerOptimizationType, DeviceSettings, FrequencyUnit, InventorySettings } from 'api/interfaces'
import { AdPlacementType } from 'api/interfaces/CampaignDelivery'
import { campaignsApi, useGetCampaignQuery, useUpdateAudienceFootprintMutation, useUpdateCampaignDeliveryMutation, useUpdateCampaignLocationTargetingMutation, useUpdateCampaignMutation, useUpdateTargetCpmMutation } from 'api/rtkQueryApi/platform/campaignsApi'
import { showErrorToast, showSuccessToast } from 'app/appSlice'
import { RootState } from 'app/rootReducer'
import { AcFileUploader } from 'components/Basic/AcFileUploader'
import BudgetChangeModal from 'components/Modals/BudgetChangeModal'
import { DefaultDayParts } from 'components/targeters/DayPartPicker'
import { LocationType } from 'components/targeters/GeoSelectionTargeter'
import { FootprintFormRow, emptyFootprint } from 'features/builders/agency/FootprintSelector'
import { BbMinTotalBudget, BbMinWeeklyBudget, BudgetMethod, CampaignBudget, CampaignBudgetSetter, InternetMinTotalBudget, InternetMinWeeklyBudget, TvMinTotalBudget, TvMinWeeklyBudget } from 'features/campaigns/CampaignBudgetSetter'
import { useCampaignDetailsEdit } from 'features/campaigns/campaign/CampaignDetailsEditContext'
import UnpaidCallOut from 'features/campaigns/campaign/UnpaidCallOut'
import { fetchCampaign } from 'features/campaigns/campaign/campaignSlice'
import { getUploadedAudienceFileName } from 'utils/uploadedAudienceFileName'
import { useWhiteLabel } from 'whiteLabel/WhiteLabelContext'

export interface ICampaignDetailsDrawer {
  campaign: Campaign
}

interface CampaignDetailsFormState {
  name: string
  campaignBudget: CampaignBudget
  maxCpm: number | undefined
  targetCpm: number | undefined
  frequency: number
  footprint: AudienceFootprint
  audienceUrl: string
  inventorySettings: InventorySettings
  deviceSettings: DeviceSettings
}

const isSameDay = (left: moment.Moment | undefined, right: moment.Moment | undefined) => (!left && !right) || (!!left && !!right && left.isSame(right, 'day'))

const isSameStateList = (left: string[], right: string[]) => left.length === right.length && left.every(state => right.includes(state))

const isSameInventory = (left: InventorySettings, right: InventorySettings) => left.targetApp === right.targetApp && left.targetWeb === right.targetWeb

const isSameDevices = (left: DeviceSettings, right: DeviceSettings) => left.targetPc === right.targetPc && left.targetPhone === right.targetPhone && left.targetTablet === right.targetTablet

const areFormStatesEqual = (left: CampaignDetailsFormState, right: CampaignDetailsFormState) =>
  left.name === right.name &&
  left.campaignBudget.budget === right.campaignBudget.budget &&
  left.campaignBudget.method === right.campaignBudget.method &&
  isSameDay(left.campaignBudget.start, right.campaignBudget.start) &&
  isSameDay(left.campaignBudget.end, right.campaignBudget.end) &&
  left.maxCpm === right.maxCpm &&
  left.targetCpm === right.targetCpm &&
  left.frequency === right.frequency &&
  left.audienceUrl === right.audienceUrl &&
  isSameStateList(left.footprint.states, right.footprint.states) &&
  isSameInventory(left.inventorySettings, right.inventorySettings) &&
  isSameDevices(left.deviceSettings, right.deviceSettings)

export const CampaignDetailsDrawer: React.FC<ICampaignDetailsDrawer> = props => {
  const campaignQuery = useGetCampaignQuery({ accountId: props.campaign.accountId, campaignId: props.campaign.id })
  const [updateCampaign, updateCampaignQuery] = useUpdateCampaignMutation()
  const [updateTargetCpm, updateTargetCpmQuery] = useUpdateTargetCpmMutation()
  const [updateCampaignDelivery, updateCampaignDeliveryQuery] = useUpdateCampaignDeliveryMutation()
  const [updateAudienceFootprint, updateAudienceFootprintQuery] = useUpdateAudienceFootprintMutation()
  const [updateLocationTargeting, updateLocationTargetingQuery] = useUpdateCampaignLocationTargetingMutation()
  const [footprint, setFootprint] = useState<AudienceFootprint>(emptyFootprint())
  const [audienceUrl, setAudienceUrl] = useState<string>('')
  const [isAudienceUploadValid, setIsAudienceUploadValid] = useState(true)
  const [frequency, setFrequency] = useState<number>(0)
  const [inventorySettings, setInventorySettings] = useState<InventorySettings>({ targetApp: false, targetWeb: true })
  const [deviceSettings, setDeviceSettings] = useState<DeviceSettings>({ targetPc: true, targetPhone: true, targetTablet: true })
  const isEnterpriseOrganization = useEnterpriseSupport()
  const audienceUploadLimit = useAudienceUploadLimit()
  const dispatch = useDispatch()
  const [amount, setAmount] = useState<number>(0)
  const [isValid, setIsValid] = useState<boolean>(true)
  const { currentAccount, isVendastaUser, isVendastaPartnerUser, isStaffUser } = useSelector((state: RootState) => state.app)

  const [name, setName] = useState<string>('')
  const [maxCpm, setMaxCpm] = useState<number | undefined>()
  const [targetCpm, setTargetCpm] = useState<number | undefined>()

  const [isBudgetChangeModalVisible, setIsBudgetChangeModalVisible] = useState(false)
  const [campaignBudget, setCampaignBudget] = useState<CampaignBudget>({
    budget: 0,
    method: BudgetMethod.Indefinite
  })
  const whiteLabel = useWhiteLabel()
  const { setHasUnsavedChanges: publishHasUnsavedChanges, setCanSaveChanges: publishCanSaveChanges, setActions: publishActions } = useCampaignDetailsEdit()

  const usesBudgetDates = whiteLabel?.isAgencies() || isEnterpriseOrganization

  const buildFormStateFromCampaign = (campaign: Campaign): CampaignDetailsFormState => ({
    name: campaign.name,
    campaignBudget: {
      budget: campaign.budget,
      start: campaign.startDate ? moment.utc(campaign.startDate) : undefined,
      end: campaign.endDate ? moment.utc(campaign.endDate) : undefined,
      method: usesBudgetDates && campaign.startDate ? BudgetMethod.Dates : BudgetMethod.Indefinite
    },
    maxCpm: campaign.maxCpm,
    targetCpm: campaign.targetCpm,
    frequency: campaign.advancedTargeting?.frequency ?? 0,
    footprint: campaign.audienceFootprint ?? emptyFootprint(),
    audienceUrl: campaign.advancedTargeting?.locations?.uploadedAudienceUrl ?? '',
    inventorySettings: { targetApp: campaign.advancedTargeting?.inventorySettings?.targetApp ?? false, targetWeb: campaign.advancedTargeting?.inventorySettings?.targetWeb ?? true },
    deviceSettings: { targetPc: campaign.advancedTargeting?.deviceSettings?.targetPc ?? true, targetPhone: campaign.advancedTargeting?.deviceSettings?.targetPhone ?? true, targetTablet: campaign.advancedTargeting?.deviceSettings?.targetTablet ?? true }
  })

  const applyFormState = (formState: CampaignDetailsFormState) => {
    setCampaignBudget(formState.campaignBudget)
    setName(formState.name)
    setMaxCpm(formState.maxCpm)
    setTargetCpm(formState.targetCpm)
    setFrequency(formState.frequency)
    setFootprint(formState.footprint)
    setAudienceUrl(formState.audienceUrl)
    setInventorySettings(formState.inventorySettings)
    setDeviceSettings(formState.deviceSettings)
  }

  const resetFormFromCampaign = () => {
    if (campaignQuery.data) {
      applyFormState(buildFormStateFromCampaign(campaignQuery.data))
    }
  }

  const currentFormState: CampaignDetailsFormState = { name, campaignBudget, maxCpm, targetCpm, frequency, footprint, audienceUrl, inventorySettings, deviceSettings }
  const hasUnsavedChanges = !!campaignQuery.data && !areFormStatesEqual(currentFormState, buildFormStateFromCampaign(campaignQuery.data))

  const budgetIncrease = campaignQuery.data ? campaignBudget.budget - campaignQuery.data.budget : 0
  const requiresBudgetChangeConfirmation = !!campaignQuery.data && budgetIncrease > 0 && !campaignQuery.data.canRemove && campaignQuery.data.activeStatus === CampaignActiveStatus.Active

  useEffect(() => {
    resetFormFromCampaign()
  }, [campaignQuery.data, usesBudgetDates])

  useEffect(() => {
    setIsValid(isValidBudget(campaignBudget))
  }, [campaignBudget])

  useEffect(() => {
    publishHasUnsavedChanges(hasUnsavedChanges)
  }, [hasUnsavedChanges, publishHasUnsavedChanges])

  useEffect(() => {
    publishCanSaveChanges(isValid && !requiresBudgetChangeConfirmation)
  }, [isValid, requiresBudgetChangeConfirmation, publishCanSaveChanges])

  useEffect(() => {
    publishActions({ save: persistCampaignDetails, discard: resetFormFromCampaign })
  })

  useEffect(() => {
    return () => {
      publishActions(null)
      publishHasUnsavedChanges(false)
      publishCanSaveChanges(true)
    }
  }, [publishActions, publishHasUnsavedChanges, publishCanSaveChanges])

  const onBudgetChangeModalComplete = (shouldFetchCampaign: boolean) => {
    if (shouldFetchCampaign) {
      dispatch(fetchCampaign(currentAccount!.id, props.campaign.id))
      dispatch(campaignsApi.util.invalidateTags([{ type: 'Campaign', id: props.campaign.id }]))
    }
    setIsBudgetChangeModalVisible(false)
  }

  const onBudgetChangeModalCancel = () => {
    setIsBudgetChangeModalVisible(false)
  }

  const showBudgetChangeModal = (amount: number) => {
    if (campaignQuery.data) {
      setAmount(amount)
      setIsBudgetChangeModalVisible(true)
    }
  }

  const persistCampaignDetails = async () => {
    if (!campaignQuery.data) {
      return
    }
    await updateCampaign({ accountId: currentAccount!.id, campaignId: campaignQuery.data.id, name: name, budget: campaignBudget.budget, maxCpm, startDate: campaignBudget.start, endDate: campaignBudget.end }).unwrap()
    await saveTargetCpm()
    await saveDelivery()
    await saveAudience()
    await saveFootprint()
    dispatch(campaignsApi.util.invalidateTags([{ type: 'Campaign', id: campaignQuery.data.id }]))
  }

  const onSave = () => {
    if (!campaignQuery.data) {
      return
    }
    if (requiresBudgetChangeConfirmation) {
      showBudgetChangeModal(budgetIncrease)
    } else {
      persistCampaignDetails()
        .then(() => {
          dispatch(showSuccessToast('Saved Campaign'))
          dispatch(fetchCampaign(currentAccount!.id, props.campaign.id))
        })
        .catch(() => dispatch(showErrorToast('Failed to save campaign')))
    }
  }

  const canEditTargetCpm = isEnterpriseOrganization && props.campaign.type === CampaignType.Internet && campaignQuery.data?.activeStatus === CampaignActiveStatus.Inactive

  const currentAudienceUrl = campaignQuery.data?.advancedTargeting?.locations?.uploadedAudienceUrl ?? ''
  const hasAudienceChanged = audienceUrl.length > 0 && audienceUrl !== currentAudienceUrl

  const onAudienceUploaded = (url: string) => {
    if (url) {
      setAudienceUrl(url)
    }
  }

  const saveAudience = async () => {
    const campaign = campaignQuery.data
    const advancedTargeting = campaign?.advancedTargeting
    if (!canEditTargetCpm || !campaign || !advancedTargeting || !hasAudienceChanged) {
      return
    }

    const locations = advancedTargeting.locations ?? { countries: [], regions: [], dmas: [], cities: [], postalCodes: [] }
    await updateLocationTargeting({
      accountId: currentAccount!.id,
      campaignId: campaign.id,
      targeting: {
        locations: { ...locations, uploadedAudienceUrl: audienceUrl },
        geoCircles: advancedTargeting.geoCircles ?? [],
        geoRectangles: advancedTargeting.geoRectangles ?? [],
        geoPolygons: advancedTargeting.geoPolygons ?? [],
        geoFences: advancedTargeting.geoFences ?? []
      },
      locationType: (advancedTargeting.locationType ?? LocationType.State) as LocationType,
      audience: {
        ageRanges: advancedTargeting.ageRanges ?? [],
        genders: advancedTargeting.genders ?? [],
        householdIncomes: advancedTargeting.householdIncomes ?? [],
        thirdPartyAudiences: advancedTargeting.audiences ?? [],
        segments: advancedTargeting.segments ?? []
      }
    }).unwrap()
  }

  const saveFootprint = async () => {
    const campaign = campaignQuery.data
    if (!canEditTargetCpm || !campaign) {
      return
    }

    const currentStates = campaign.audienceFootprint?.states ?? []
    const unchanged = currentStates.length === footprint.states.length && footprint.states.every(state => currentStates.includes(state))
    if (unchanged) {
      return
    }

    await updateAudienceFootprint({ accountId: currentAccount!.id, campaignId: campaign.id, footprint }).unwrap()
  }

  const hasDeliveryChanged = () => {
    const advancedTargeting = campaignQuery.data?.advancedTargeting
    if (!advancedTargeting) {
      return true
    }

    const inventoryChanged = (advancedTargeting.inventorySettings?.targetApp ?? false) !== inventorySettings.targetApp || (advancedTargeting.inventorySettings?.targetWeb ?? true) !== inventorySettings.targetWeb
    const deviceChanged = (advancedTargeting.deviceSettings?.targetPc ?? true) !== deviceSettings.targetPc || (advancedTargeting.deviceSettings?.targetPhone ?? true) !== deviceSettings.targetPhone || (advancedTargeting.deviceSettings?.targetTablet ?? true) !== deviceSettings.targetTablet
    return (advancedTargeting.frequency ?? 0) !== frequency || inventoryChanged || deviceChanged
  }

  const saveDelivery = async () => {
    if (!canEditTargetCpm || !campaignQuery.data || !hasDeliveryChanged()) {
      return
    }

    const campaign = campaignQuery.data
    await updateCampaignDelivery({
      accountId: currentAccount!.id,
      campaignId: campaign.id,
      delivery: {
        inventorySettings,
        deviceSettings,
        frequency,
        frequencyUnit: campaign.advancedTargeting?.frequencyUnit ?? FrequencyUnit.week,
        dayParts: campaign.advancedTargeting?.dayParts ?? DefaultDayParts,
        categories: campaign.categories,
        placement: AdPlacementType.Everywhere,
        specific: []
      },
      customerOptimizationStrategy: campaign.customerOptimizationStrategy ?? { optimizationType: CustomerOptimizationType.QualityImpressions }
    }).unwrap()
  }

  const saveTargetCpm = async () => {
    if (!canEditTargetCpm || !campaignQuery.data || targetCpm === campaignQuery.data.targetCpm) {
      return
    }

    await updateTargetCpm({ accountId: currentAccount!.id, campaignId: campaignQuery.data.id, targetCpm }).unwrap()
  }

  const getMinWeeklyBudget = () => {
    if (props.campaign.type === CampaignType.Internet) {
      return InternetMinWeeklyBudget
    } else if (props.campaign.type === CampaignType.TV) {
      return TvMinWeeklyBudget
    } else if (props.campaign.type === CampaignType.Billboard) {
      return BbMinWeeklyBudget
    } else if (props.campaign.type === CampaignType.Audio) {
      return TvMinWeeklyBudget
    }
    return TvMinWeeklyBudget
  }

  const getMinTotalBudget = () => {
    if (props.campaign.type === CampaignType.Internet) {
      return InternetMinTotalBudget
    } else if (props.campaign.type === CampaignType.TV) {
      return TvMinTotalBudget
    } else if (props.campaign.type === CampaignType.Billboard) {
      return BbMinTotalBudget
    } else if (props.campaign.type === CampaignType.Audio) {
      return TvMinTotalBudget
    }
    return TvMinTotalBudget
  }

  const getMinStartDate = () => {
    if (!props.campaign?.activated || !props.campaign.startDate) {
      return isStaffUser ? moment() : moment().add(3, 'day')
    }

    const startDate = moment(props.campaign.startDate)
    if (startDate < moment()) {
      return startDate
    }

    return moment().add(3, 'day') < startDate ? moment().add(3, 'day') : startDate
  }

  const isValidBudget = (budget: CampaignBudget): boolean => {
    if (budget.method === BudgetMethod.Indefinite) {
      return budget.budget >= getMinWeeklyBudget() && !budget.start && !budget.end
    } else if (budget.method === BudgetMethod.Dates) {
      return !!(budget.budget >= getMinTotalBudget() && budget.start && budget.end && moment(budget.start).isBefore(budget.end) && (moment(budget.start).isSameOrAfter(moment(props.campaign.startDate), 'day') || moment(budget.start).endOf('day').isAfter(getMinStartDate().startOf('day'))))
    }

    return false
  }

  const onBudgetChange = (budget: CampaignBudget) => {
    setCampaignBudget(budget)
  }

  if (campaignQuery.isLoading || !campaignQuery.data) {
    return <EuiLoadingContent lines={10} />
  }

  return (
    <React.Fragment>
      <EuiSpacer />
      <EuiFlexGroup>
        <EuiFlexItem>
          <React.Fragment>
            {campaignQuery.data.status === CampaignStatus.PaymentFailed && !campaignQuery.data.isUnpaidSubscriptionPayment && (
              <React.Fragment>
                <UnpaidCallOut campaignId={campaignQuery.data.id} hasBundle={campaignQuery.data.hasBundle} />
                <EuiSpacer />
              </React.Fragment>
            )}
            <EuiFormRow label='Campaign Name'>
              <EuiFieldText name='name' value={name} onChange={e => setName(e.target.value)} placeholder='' fullWidth />
            </EuiFormRow>
            <EuiSpacer />
            {(!isVendastaUser || isVendastaPartnerUser) && campaignQuery.data && campaignBudget && <CampaignBudgetSetter campaign={campaignQuery.data} campaignBudget={campaignBudget} onBudgetChange={onBudgetChange} />}
            {canEditTargetCpm && (
              <EuiFormRow label='Target Audience' helpText={`Upload a csv under ${audienceUploadLimit.label} to replace the current list`}>
                <React.Fragment>
                  {audienceUrl && (
                    <EuiText size='s'>
                      <EuiLink href={audienceUrl} target='_blank'>
                        {getUploadedAudienceFileName(audienceUrl)}
                      </EuiLink>
                      {hasAudienceChanged && ' (new, save to apply)'}
                    </EuiText>
                  )}
                  <EuiSpacer size='s' />
                  <AcFileUploader maxFileSize={audienceUploadLimit.maxFileSize} allowedFileTypes={['text/plain', 'text/csv']} onUploadedUrlChange={onAudienceUploaded} onIsValidFileChange={setIsAudienceUploadValid} />
                  {!isAudienceUploadValid && <EuiCallOut title={`Please upload a csv under ${audienceUploadLimit.label}`} iconType='document' color='warning' size='s' />}
                </React.Fragment>
              </EuiFormRow>
            )}
            {canEditTargetCpm && audienceUrl && <FootprintFormRow value={footprint} onChange={setFootprint} />}
            {canEditTargetCpm && (
              <EuiFormRow label='Delivery'>
                <EuiFlexGrid columns={2} gutterSize='s'>
                  <EuiFlexItem>
                    <EuiFieldNumber compressed fullWidth value={frequency} step={1} min={0} onChange={e => setFrequency(e.target.value === '' ? 0 : parseInt(e.target.value))} append='views / user' />
                  </EuiFlexItem>
                  <EuiFlexItem>
                    <EuiFieldNumber compressed prepend='$' fullWidth step={0.01} min={0} value={targetCpm ?? ''} onChange={e => setTargetCpm(e.target.value === '' ? undefined : parseFloat(e.target.value))} append='cpm' />
                  </EuiFlexItem>
                  <EuiFlexItem>
                    <EuiCheckbox id='detailsTargetApp' checked={inventorySettings.targetApp} onChange={() => setInventorySettings({ ...inventorySettings, targetApp: !inventorySettings.targetApp })} label='Target App' />
                    <EuiCheckbox id='detailsTargetWeb' checked={inventorySettings.targetWeb} onChange={() => setInventorySettings({ ...inventorySettings, targetWeb: !inventorySettings.targetWeb })} label='Target Web' />
                  </EuiFlexItem>
                  <EuiFlexItem>
                    <EuiCheckbox id='detailsTargetPc' checked={deviceSettings.targetPc} onChange={() => setDeviceSettings({ ...deviceSettings, targetPc: !deviceSettings.targetPc })} label='Target PC' />
                    <EuiCheckbox id='detailsTargetPhone' checked={deviceSettings.targetPhone} onChange={() => setDeviceSettings({ ...deviceSettings, targetPhone: !deviceSettings.targetPhone })} label='Target Phone' />
                    <EuiCheckbox id='detailsTargetTablet' checked={deviceSettings.targetTablet} onChange={() => setDeviceSettings({ ...deviceSettings, targetTablet: !deviceSettings.targetTablet })} label='Target Tablet' />
                  </EuiFlexItem>
                </EuiFlexGrid>
              </EuiFormRow>
            )}
            <EuiFormRow>
              <EuiButton id='save' fill onClick={onSave} disabled={!isValid} isLoading={updateCampaignQuery.isLoading || updateTargetCpmQuery.isLoading || updateCampaignDeliveryQuery.isLoading || updateAudienceFootprintQuery.isLoading || updateLocationTargetingQuery.isLoading}>
                Save
              </EuiButton>
            </EuiFormRow>
          </React.Fragment>
        </EuiFlexItem>
        {!isEnterpriseOrganization && (
          <EuiFlexItem grow={false} style={{ width: 260 }}>
            <EuiText size='xs'>
              <h3>Best Practices</h3>
              <h5>Running Campaigns</h5>
              <p>While campaigns can be edited anytime, it is generally a best practice to change things slowly and carefully. For example, making frequent changes to a campaign's targeting or budget settings can hinder the machine learning and optimization algorithms.</p>
            </EuiText>
          </EuiFlexItem>
        )}
      </EuiFlexGroup>
      <BudgetChangeModal campaign={campaignQuery.data} isBudgetChangeModalVisible={isBudgetChangeModalVisible} name={name} budget={campaignBudget.budget} differenceAmount={amount} onConfirmComplete={onBudgetChangeModalComplete} onCancel={onBudgetChangeModalCancel} />
    </React.Fragment>
  )
}
